'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Play, Star, Clock, Crown, Heart, Plus } from 'lucide-react';

interface Movie {
  id: string;
  title: string;
  year: number;
  rating: number;
  duration: string;
  genre: string;
  poster: string;
  posterAlt: string;
  isPremium: boolean;
  isNew: boolean;
  views: number;
}

interface MovieCardProps {
  movie: Movie;
  onPlay: () => void;
}

function formatViews(views: number): string {
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
  if (views >= 1000) return `${(views / 1000).toFixed(0)}K`;
  return views.toString();
}

export default function MovieCard({ movie, onPlay }: MovieCardProps) {
  const [hovered, setHovered] = useState(false);
  const [liked, setLiked] = useState(false);

  return (
    <div
      className="group relative bg-card rounded-xl overflow-hidden border border-border transition-all duration-200 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Poster */}
      <div className="relative aspect-[2/3] overflow-hidden">
        <AppImage
          src={movie.poster}
          alt={movie.posterAlt}
          fill
          className={`object-cover transition-transform duration-300 ${hovered ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 16vw"
        />
        <div className="poster-overlay absolute inset-0" />

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {movie.isPremium && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-accent/90 text-accent-foreground text-xs font-700 rounded-md">
              <Crown size={9} />
              PRO
            </span>
          )}
          {movie.isNew && (
            <span className="px-1.5 py-0.5 bg-primary/90 text-primary-foreground text-xs font-700 rounded-md">
              I RI
            </span>
          )}
        </div>

        {/* Quick actions on hover */}
        <div
          className={`absolute top-2 right-2 flex flex-col gap-1.5 transition-all duration-200 ${
            hovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
          }`}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLiked(!liked);
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-lg backdrop-blur-sm transition-all ${
              liked
                ? 'bg-primary/80 text-primary-foreground'
                : 'bg-black/60 text-foreground hover:bg-black/80'
            }`}
            aria-label="Shto te preferuarat"
          >
            <Heart size={12} className={liked ? 'fill-primary-foreground' : ''} />
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-black/60 text-foreground backdrop-blur-sm hover:bg-black/80 transition-all"
            aria-label="Shto në listë"
          >
            <Plus size={12} />
          </button>
        </div>

        {/* Play button overlay */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
            hovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={onPlay}
            className="w-12 h-12 flex items-center justify-center bg-primary rounded-full shadow-lg shadow-primary/40 hover:bg-primary/90 active:scale-95 transition-all"
            aria-label={`Luaj ${movie.title}`}
          >
            <Play size={20} className="fill-primary-foreground text-primary-foreground ml-0.5" />
          </button>
        </div>

        {/* Rating + duration at bottom */}
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
          <span className="flex items-center gap-1 text-accent text-xs font-700">
            <Star size={11} className="fill-accent" />
            {movie.rating}
          </span>
          <span className="flex items-center gap-1 text-foreground/80 text-xs font-500">
            <Clock size={10} />
            {movie.duration}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-2.5">
        <h3 className="text-sm font-600 text-foreground line-clamp-1">{movie.title}</h3>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground">{movie.year}</span>
          <span className="text-xs text-muted-foreground">{formatViews(movie.views)} shikime</span>
        </div>
        <span className="mt-1 inline-block text-xs text-secondary-foreground bg-muted px-2 py-0.5 rounded-md border border-border">
          {movie.genre}
        </span>
      </div>
    </div>
  );
}